import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useCurrentUserAccess from "../../hooks/useCurrentUserAccess";
import { authFetch } from "../../utils/authFetch";
import { clearAuthSession, getStoredUser } from "../../utils/authStorage";
import { getHostContext } from "../../utils/hostContext";
import "./Topbar.css";

const THEME_STORAGE_KEY = "agentBookTheme";

const BellIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    aria-hidden="true"
    style={{ display: "block" }}
    className="topbar-bell-icon"
  >
    <path
      d="M12 3a4 4 0 0 0-4 4v1.1c0 .7-.2 1.3-.6 1.9L6.2 12c-.8 1.1-1.2 2.4-1.2 3.8V17h14v-1.2c0-1.4-.4-2.7-1.2-3.8l-1.2-2c-.4-.6-.6-1.2-.6-1.9V7a4 4 0 0 0-4-4Zm0 18a2.7 2.7 0 0 1-2.4-1.5h4.8A2.7 2.7 0 0 1 12 21Z"
      fill="currentColor"
    />
  </svg>
);

const Topbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, platformRole, accountRole } = useCurrentUserAccess();

  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [launchingDemo, setLaunchingDemo] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchMenuOpen, setSearchMenuOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationActionLoading, setNotificationActionLoading] = useState(false);

  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    if (savedTheme === "dark" || savedTheme === "light") {
      return savedTheme;
    }

    return window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  const topbarRef = useRef(null);
  const searchRef = useRef(null);
  const notificationsRef = useRef(null);
  const actionsRef = useRef(null);

  const storedUser = useMemo(() => getStoredUser(), []);

  const { isTenantSubdomain } = getHostContext();
  const demoSession = localStorage.getItem("demoSession");

  const isSubdomainHost = isTenantSubdomain;

  const isDemoArea =
    Boolean(demoSession) ||
    location.pathname === "/demo-accounts" ||
    location.pathname === "/demo-accounts/new" ||
    /^\/demo-accounts\/[^/]+$/.test(location.pathname);

  const isPlatformAdmin = platformRole?.key_name === "platform_admin";
  const isPlatformEmployee = platformRole?.key_name === "platform_employee";
  const isAccountAdmin = accountRole?.key_name === "account_admin";
  const isAccountUser = accountRole?.key_name === "account_user";

  const isPlatformSide =
    !isSubdomainHost && !isDemoArea && (isPlatformAdmin || isPlatformEmployee);

  const isAccountSide =
    (isSubdomainHost && !isDemoArea) || isAccountAdmin || isAccountUser;

  const userDisplayName = loading
    ? "Loading..."
    : storedUser?.name || storedUser?.full_name || storedUser?.first_name || "Agent";

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.is_read).length,
    [notifications]
  );

  useEffect(() => {
    document.body.classList.toggle("dark-mode", theme === "dark");
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!event.target.closest(".topbar-dropdown-wrap")) {
        setIsCreateMenuOpen(false);
      }

      if (!event.target.closest(".topbar-search-wrap")) {
        setSearchMenuOpen(false);
      }

      if (!event.target.closest(".topbar-notification-wrap")) {
        setNotificationsOpen(false);
      }

      if (topbarRef.current && !topbarRef.current.contains(event.target)) {
        setMobileSearchOpen(false);
        setMobileActionsOpen(false);
      }

      if (
        actionsRef.current &&
        !actionsRef.current.contains(event.target) &&
        !event.target.closest(".topbar-overflow-btn")
      ) {
        setMobileActionsOpen(false);
      }
    };

    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  useEffect(() => {
    setMobileSearchOpen(false);
    setMobileActionsOpen(false);
    setSearchMenuOpen(false);
    setNotificationsOpen(false);
    setIsCreateMenuOpen(false);
  }, [location.pathname]);

  const loadNotifications = async () => {
    try {
      setNotificationsLoading(true);

      await authFetch("/api/notifications/sync", {
        method: "POST"
      });

      const res = await authFetch("/api/notifications");
      const data = res.ok ? await res.json() : [];
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Topbar notifications load error:", err.message);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    const runSearch = async () => {
      const value = searchValue.trim();

      if (!value) {
        setSearchResults([]);
        return;
      }

      const lower = value.toLowerCase();
      const quickLinks = [
        { id: "dashboard", label: "Dashboard", subtitle: "Open dashboard", route: "/dashboard" },
        { id: "leads", label: "Leads", subtitle: "Go to leads", route: "/leads" },
        { id: "clients", label: "Clients", subtitle: "Go to clients", route: "/clients" },
        { id: "tasks", label: "Tasks", subtitle: "Go to tasks", route: "/tasks" },
        { id: "calendar", label: "Calendar", subtitle: "Open calendar", route: "/calendar" },
        { id: "policies", label: "Policies", subtitle: "Go to policies", route: "/policies" },
        { id: "opportunities", label: "Opportunities", subtitle: "Go to opportunities", route: "/opportunities" }
      ].filter(
        (item) =>
          item.label.toLowerCase().includes(lower) ||
          item.subtitle.toLowerCase().includes(lower)
      );

      if (value.length < 2) {
        setSearchResults(quickLinks);
        return;
      }

      try {
        setSearchLoading(true);

        const [leadsRes, clientsRes] = await Promise.all([
          authFetch("/api/leads"),
          authFetch("/api/clients")
        ]);

        const leads = leadsRes.ok ? await leadsRes.json() : [];
        const clients = clientsRes.ok ? await clientsRes.json() : [];

        const matchedLeads = (Array.isArray(leads) ? leads : [])
          .filter((lead) => {
            const fullName = `${lead.first_name || ""} ${lead.last_name || ""}`.trim().toLowerCase();
            const email = String(lead.email || "").toLowerCase();
            const phone = String(lead.phone || "").toLowerCase();
            return fullName.includes(lower) || email.includes(lower) || phone.includes(lower);
          })
          .slice(0, 4)
          .map((lead) => ({
            id: `lead-${lead.id}`,
            label: `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || "Lead",
            subtitle: `Lead${lead.email ? ` • ${lead.email}` : ""}`,
            route: `/lead-profile/${lead.id}`
          }));

        const matchedClients = (Array.isArray(clients) ? clients : [])
          .filter((client) => {
            const fullName = `${client.first_name || ""} ${client.last_name || ""}`.trim().toLowerCase();
            const email = String(client.email || "").toLowerCase();
            const phone = String(client.phone || "").toLowerCase();
            return fullName.includes(lower) || email.includes(lower) || phone.includes(lower);
          })
          .slice(0, 4)
          .map((client) => ({
            id: `client-${client.id}`,
            label: `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Client",
            subtitle: `Client${client.email ? ` • ${client.email}` : ""}`,
            route: `/client-profile/${client.id}`
          }));

        setSearchResults([...quickLinks, ...matchedLeads, ...matchedClients].slice(0, 10));
      } catch (err) {
        console.error("Topbar search error:", err.message);
        setSearchResults(quickLinks);
      } finally {
        setSearchLoading(false);
      }
    };

    const timeout = setTimeout(runSearch, 220);
    return () => clearTimeout(timeout);
  }, [searchValue]);

  const handleThemeToggle = (event) => {
    event.stopPropagation();
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const handleMobileSidebarToggle = (event) => {
    event.stopPropagation();
    setMobileSearchOpen(false);
    setMobileActionsOpen(false);
    window.dispatchEvent(new Event("toggle-mobile-sidebar"));
  };

  const handleMobileSearchToggle = (event) => {
    event.stopPropagation();
    setMobileActionsOpen(false);
    setNotificationsOpen(false);
    setMobileSearchOpen((prev) => !prev);
  };

  const handleMobileActionsToggle = (event) => {
    event.stopPropagation();
    setMobileSearchOpen(false);
    setSearchMenuOpen(false);
    setNotificationsOpen(false);
    setMobileActionsOpen((prev) => !prev);
  };

  const handleLogout = () => {
    clearAuthSession();
    setMobileActionsOpen(false);
    navigate("/login");
  };

  const handleCreate = (type) => {
    setIsCreateMenuOpen(false);
    setMobileActionsOpen(false);

    switch (type) {
      case "sales-lead":
        navigate("/new-sales-lead");
        break;
      case "subscriber":
        navigate("/new-subscriber");
        break;
      case "demo-account":
        navigate("/demo-accounts/new");
        break;
      case "lead":
        navigate("/new-lead");
        break;
      case "client":
        navigate("/new-client");
        break;
      case "task":
        navigate("/tasks");
        break;
      case "user":
        navigate("/users/new");
        break;
      default:
        break;
    }
  };

  const handleOpenDemo = async () => {
    let demoWindow = null;

    try {
      setLaunchingDemo(true);
      setShowDemoPopup(true);
      setMobileActionsOpen(false);

      demoWindow = window.open("", "_blank", "noopener,noreferrer");

      const res = await authFetch("/api/demo-accounts/launch-mine", {
        method: "POST"
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to launch demo");
      }

      if (!data.launch_url) {
        throw new Error("Launch URL was not returned.");
      }

      setTimeout(() => {
        if (demoWindow) {
          demoWindow.location.href = data.launch_url;
          demoWindow.focus();
        } else {
          window.open(data.launch_url, "_blank", "noopener,noreferrer");
        }

        setShowDemoPopup(false);
      }, 900);
    } catch (err) {
      console.error("Launch assigned demo error:", err.message);

      if (demoWindow && !demoWindow.closed) {
        demoWindow.close();
      }

      setShowDemoPopup(false);
      alert(err.message);
    } finally {
      setLaunchingDemo(false);
    }
  };

  const handleSearchPick = (route) => {
    setSearchMenuOpen(false);
    setMobileSearchOpen(false);
    setSearchValue("");
    navigate(route);
  };

  const handleNotificationOpen = async (item) => {
    try {
      await authFetch(`/api/notifications/${item.id}/read`, {
        method: "POST"
      });

      setNotifications((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, is_read: 1 } : n))
      );

      setNotificationsOpen(false);
      navigate(item.link_url || "/dashboard");
    } catch (err) {
      console.error("Notification open error:", err.message);
      navigate(item.link_url || "/dashboard");
    }
  };

  const handleDeleteNotification = async (event, id) => {
    event.stopPropagation();

    try {
      setNotificationActionLoading(true);

      const res = await authFetch(`/api/notifications/${id}`, {
        method: "DELETE"
      });

      if (!res.ok) throw new Error("Failed to delete notification");

      setNotifications((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      alert(err.message);
    } finally {
      setNotificationActionLoading(false);
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      setNotificationActionLoading(true);

      const res = await authFetch("/api/notifications/clear-all", {
        method: "POST"
      });

      if (!res.ok) throw new Error("Failed to clear notifications");

      setNotifications([]);
    } catch (err) {
      alert(err.message);
    } finally {
      setNotificationActionLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setNotificationActionLoading(true);

      const res = await authFetch("/api/notifications/read-all", {
        method: "POST"
      });

      if (!res.ok) throw new Error("Failed to mark all read");

      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: 1 })));
    } catch (err) {
      alert(err.message);
    } finally {
      setNotificationActionLoading(false);
    }
  };

  const quickActions = useMemo(() => {
    if (isPlatformSide) {
      return [
        { key: "sales-lead", label: "New Sales Lead" },
        { key: "subscriber", label: "New Subscriber" },
        { key: "demo-account", label: "New Demo Account" },
        { key: "user", label: "New User" },
        { key: "task", label: "New Task" }
      ];
    }

    if (isAccountSide) {
      return [
        { key: "lead", label: "New Lead" },
        { key: "client", label: "New Client" },
        { key: "user", label: "New User" },
        { key: "task", label: "New Task" }
      ];
    }

    return [{ key: "task", label: "New Task" }];
  }, [isPlatformSide, isAccountSide]);

  const topbarClassName = [
    "topbar",
    mobileSearchOpen ? "topbar-search-open" : "",
    mobileActionsOpen ? "topbar-actions-open" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {showDemoPopup && (
        <div className="topbar-demo-overlay">
          <div className="topbar-demo-card">
            <h3>Entering Demonstration Mode</h3>
            <p>Opening your demo workspace...</p>
          </div>
        </div>
      )}

      <div className={topbarClassName} ref={topbarRef}>
        <div className="topbar-left">
          <button
            type="button"
            className="topbar-menu-btn"
            aria-label="Open navigation menu"
            onClick={handleMobileSidebarToggle}
          >
            ☰
          </button>

          <div className="topbar-search-wrap" ref={searchRef}>
            <div className="topbar-search-shell">
              <span className="topbar-search-icon">⌕</span>
              <input
                type="text"
                placeholder="Search leads, clients, pages..."
                className="topbar-search-input"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setSearchMenuOpen(true);
                }}
                onFocus={() => setSearchMenuOpen(true)}
              />
            </div>

            {searchMenuOpen && (searchValue.trim() || searchResults.length > 0) && (
              <div className="topbar-search-menu">
                {searchLoading ? (
                  <div className="topbar-search-empty">Searching...</div>
                ) : searchResults.length === 0 ? (
                  <div className="topbar-search-empty">No results found.</div>
                ) : (
                  searchResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="topbar-search-result"
                      onClick={() => handleSearchPick(item.route)}
                    >
                      <span className="topbar-search-result-title">{item.label}</span>
                      <span className="topbar-search-result-subtitle">{item.subtitle}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="topbar-right">
          <button
            type="button"
            className="topbar-search-toggle-btn"
            aria-label="Open search"
            onClick={handleMobileSearchToggle}
          >
            ⌕
          </button>

          <button
            type="button"
            className="topbar-theme-btn"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            onClick={handleThemeToggle}
          >
            {theme === "dark" ? "☀" : "🌙"}
          </button>

          <div className="topbar-notification-wrap" ref={notificationsRef}>
            <button
              className="topbar-bell-btn"
              type="button"
              onClick={() => {
                setMobileSearchOpen(false);
                setMobileActionsOpen(false);
                setNotificationsOpen((prev) => !prev);
                if (!notificationsOpen) {
                  loadNotifications();
                }
              }}
              aria-label="Notifications"
            >
              <BellIcon />
              {unreadCount > 0 && <span className="topbar-bell-dot" />}
            </button>

            {notificationsOpen && (
              <div className="topbar-notification-menu">
                <div className="topbar-notification-header-row">
                  <div className="topbar-notification-header">Notifications</div>

                  <div className="topbar-notification-header-actions">
                    <button
                      type="button"
                      className="topbar-notification-header-btn"
                      onClick={handleMarkAllRead}
                      disabled={notificationActionLoading || notifications.length === 0}
                    >
                      Mark all read
                    </button>
                    <button
                      type="button"
                      className="topbar-notification-header-btn danger"
                      onClick={handleClearAllNotifications}
                      disabled={notificationActionLoading || notifications.length === 0}
                    >
                      Clear all
                    </button>
                  </div>
                </div>

                {notificationsLoading ? (
                  <div className="topbar-notification-empty">Loading notifications...</div>
                ) : notifications.length === 0 ? (
                  <div className="topbar-notification-empty">No notifications.</div>
                ) : (
                  notifications.map((item) => (
                    <div
                      key={item.id}
                      className={`topbar-notification-item ${item.is_read ? "" : "unread"}`}
                    >
                      <button
                        type="button"
                        className="topbar-notification-open"
                        onClick={() => handleNotificationOpen(item)}
                      >
                        <span className="topbar-notification-item-title">{item.title}</span>
                        <span className="topbar-notification-item-subtitle">{item.message}</span>
                      </button>

                      <button
                        type="button"
                        className="topbar-notification-delete"
                        onClick={(event) => handleDeleteNotification(event, item.id)}
                        title="Delete notification"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="topbar-overflow-btn"
            aria-label="Open actions menu"
            onClick={handleMobileActionsToggle}
          >
            ⋯
          </button>

          <div className="topbar-actions" ref={actionsRef}>
            {isDemoArea ? (
              <div className="topbar-context-pill">Demonstration</div>
            ) : (
              isPlatformSide && (
                <button
                  className="topbar-ghost-btn"
                  onClick={handleOpenDemo}
                  type="button"
                  disabled={launchingDemo}
                >
                  {launchingDemo ? "Opening..." : "Demo"}
                </button>
              )
            )}

            <div className="topbar-dropdown-wrap">
              <button
                className="topbar-primary-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCreateMenuOpen((prev) => !prev);
                }}
                type="button"
              >
                + Create
              </button>

              {isCreateMenuOpen && (
                <div className="topbar-dropdown-menu">
                  {quickActions.map((action) => (
                    <button
                      key={action.key}
                      className="topbar-dropdown-item"
                      onClick={() => handleCreate(action.key)}
                      type="button"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="topbar-user-card">
              <div className="topbar-avatar">{userDisplayName.charAt(0)}</div>

              <div className="topbar-user-meta">
                <span className="topbar-user-title">{userDisplayName}</span>
                <span className="topbar-user-subtitle">
                  {isPlatformSide ? "Platform" : isDemoArea ? "Demo" : "Subscriber"}
                </span>
              </div>

              <button onClick={handleLogout} className="topbar-logout-btn" type="button">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Topbar;